import React, { useCallback, useEffect, useState } from 'react';

import { Spinner } from '@rbilabs/universal-components';
import { usePathname, useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useStorePosConnectivityStatus } from 'features/store/hooks/use-store-connectivity-status';
import { useIntl } from 'react-intl';

import { CardModal } from 'components/card-modal';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import CartContentControl from 'experiments/checkout-v2/control/pages/cart/cart-content';
import CartContentTreatment, {
  ICartContent,
} from 'experiments/checkout-v2/treatment/pages/cart/cart-content';
import { useEnableCheckoutV2Experiment } from 'experiments/checkout-v2/use-enable-checkout-v2-experiment';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useCart } from 'hooks/use-cart';
import useDebounceEffect from 'hooks/use-debounce-effect';
import useEffectOnce from 'hooks/use-effect-once';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { usePrevious } from 'hooks/use-previous';
import { logCheckoutEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { cartHasItemsOrCombos, shouldRedirectToMenu } from '../../utils/cart-utils';

import CartError from './cart-error';

const CartContent = (props: ICartContent) => {
  const isCheckoutV2 = useEnableCheckoutV2Experiment();
  return isCheckoutV2 ? <CartContentTreatment {...props} /> : <CartContentControl {...props} />;
};

export const Cart = () => {
  const { cartEntries, calculateCartTotalWithDiscount } = useCart();
  const {
    serverOrder,
    // TODO: BKPE-1956 - unavailableCartEntries
    unavailableCartEntries,
    orderStatus,
    cardSuccessfullyAdded,
    tryPricing,
    shouldSkipPriceOrder,
    setShouldSkipPriceOrder,
    hydrationStatus,
    isHydrationPending,
    isOrderPriceLoading,
  } = useCartContext();
  const { serviceMode, isDelivery } = useServiceModeContext();

  const { shouldConfirmStoreOnCheckout } = useUIContext();
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const { sections: addOnSections } = useFeatureMenuAddOn();
  const { checkoutPaymentMethod } = usePaymentContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const { getStoreMenu, storeMenuLoading, storeMenuCalled } = useMainMenuContext();
  const { region } = useLocale();
  const pathname = usePathname();
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const router = useRouter();
  const { isStoreOrderUnavailableDialogOpen } = useUIContext();
  const storeId = store.number || '';

  const {
    loading: isRestaurantPosConnectivityStatusLoading,
    isRestaurantPosOnline,
  } = useStorePosConnectivityStatus({
    storeId,
  });

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { isStoreAvailableForPickup, isStoreAvailableForDelivery } = useStoreAvailabilityFns();

  const { isStoreChangeHydrationInProgress } = useOrderContext();

  // DO NOT USE service mode on order context. Use service mode on server order
  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const { isCartTotalNegative } = calculateCartTotalWithDiscount();
  const isLoading = isOrderPriceLoading || isLoadingOffers;
  const unavailableCartItems = unavailableCartEntries.length > 0;
  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  // check if cart entries don't have item or combos
  const hasItemsOrCombos = cartHasItemsOrCombos(cartEntries);
  // to allow below dismissAndReplace to run with expo router which can't navigate on
  // first render if cart deep link was used
  const [isMounted, setIsMounted] = useState(false);

  useAuthRedirects({ skip: isSignUpAfterCartEnabled });

  const prevCartEntriesLength = usePrevious(cartEntries.length);

  useEffectOnce(() => {
    setIsMounted(true);
  });

  useEffect(() => {
    if (isStoreOpenAndAvailable && serviceMode && cartEntries.length > 0) {
      logCheckoutEvent({
        serviceMode,
        cartEntries,
        shouldLogIndividualProducts: !prevCartEntriesLength,
      });
    }
  }, [cartEntries, isStoreOpenAndAvailable, serviceMode, prevCartEntriesLength]);

  useDebounceEffect(
    () => {
      if (
        !isStoreOpenAndAvailable ||
        !serviceMode ||
        isHydrationPending ||
        !hasItemsOrCombos ||
        isStoreChangeHydrationInProgress
      ) {
        return;
      }

      shouldSkipPriceOrder && setShouldSkipPriceOrder(false);

      tryPricing();
    },
    250,
    // Changes on rewards should trigger a reprice. That side effect is not captured within the other deps.
    // This usually triggers a second PriceOrder, because the first attempt usually have null rewards
    // until the Redux slice sync-up with the LocalStorage (only happens on page refresh web).
    [
      appliedLoyaltyRewardsArray,
      isStoreOpenAndAvailable,
      serviceMode,
      tryPricing,
      isHydrationPending,
    ]
  );

  useEffect(() => {
    if (storeId) {
      getStoreMenu({ region, storeId });
    }
  }, [getStoreMenu, region, storeId]);

  useEffect(() => {
    setShowUnavailableItemsModal(
      !isLoading && unavailableCartItems && !isStoreOrderUnavailableDialogOpen
    );
  }, [isLoading, unavailableCartItems, isStoreOrderUnavailableDialogOpen]);

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing]);

  useEffect(() => {
    // Since multiple routes are under the same (cart) route group,
    // the cart component doesn't unmount when navigating to other routes like (/checkout), causing
    // isMounted to remain true. This ensures isMounted is only true when actually
    // on the cart route, preventing premature navigation logic execution.
    const isCartRoute = pathname === routes.cart;
    setIsMounted(isCartRoute);
  }, [pathname]);

  useEffect(() => {
    if (
      shouldRedirectToMenu({
        isMounted,
        hydrationStatus,
        cartEntries,
        orderStatus,
        serviceMode,
        orderServiceMode,
        hasItemsOrCombos,
      })
    ) {
      router.replace(routes.menu);
    }
  }, [
    router,
    isMounted,
    hydrationStatus,
    cartEntries,
    orderStatus,
    serviceMode,
    orderServiceMode,
    hasItemsOrCombos,
  ]);

  // Show the loading cart if:
  // - User is authenticated or flag enable-signup-after-cart is disabled
  // - Is restaurant online or is pricing or is getting rewards
  // - Is executing hydration
  // - Is the Store Menu Data loading or is menu service data disabled
  const loadingLegacyMenu = !isUnifiedMenuEnabled && (!storeMenuCalled || storeMenuLoading);
  const dataRelatedIsLoading =
    !isCartTotalNegative &&
    (isAuthenticated || !isSignUpAfterCartEnabled) &&
    (isRestaurantPosConnectivityStatusLoading || isHydrationPending || loadingLegacyMenu);

  if (dataRelatedIsLoading) {
    return (
      <CardModal
        key="cart"
        dismissType="back"
        invertHeaderColor
        headerText={formatMessage({ id: 'yourOrder' })}
        children={<Spinner marginTop={'50%'} />}
        webAnimate={false}
        isFullScreen
      />
    );
  }

  // TODO: We should either use these or isStoreOpenAndAvailable from the store context
  const storeAvailableForPickup = isStoreAvailableForPickup(store);
  const storeAvailableForDelivery = isStoreAvailableForDelivery(store);
  const isStoreAvailable = isDelivery ? storeAvailableForDelivery : storeAvailableForPickup;

  const handleDismissCartErrorModal = () => {
    router.replace(routes.menu);
    shouldConfirmStoreOnCheckout(true);
  };

  if (!isStoreAvailable) {
    return (
      <CartError
        onDismiss={handleDismissCartErrorModal}
        additionalLoggingContext={{
          isRestaurantPosOnline,
          shouldSkipPriceOrder,
        }}
      />
    );
  }

  if (showUnavailableItemsModal) {
    return (
      <RemoveUnavailableItemsModal
        onConfirmCb={handleUnavailableModalDismiss}
        unavailableCartEntries={unavailableCartEntries}
        isInCart
      />
    );
  }

  return (
    <>
      <CartContent
        hasAddOns={addOnSections.length > 0}
        checkoutPaymentMethod={checkoutPaymentMethod}
      />
      {cardSuccessfullyAdded && (
        <VisuallyHidden
          accessibilityLabel={formatMessage({
            id: 'yourNewPaymentMethodHasBeenSuccessfullySaved',
          })}
        />
      )}
    </>
  );
};
